
import { useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Typography,
    Box,
    InputAdornment,
    IconButton,
} from "@mui/material";

import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

import { Flip, ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { toast_config } from "../toastify/config";

import apiRc from "../../axios/axios";
import { jwtDecode } from "jwt-decode";
import LoadingSpinner from "../Spinner";


const PrimerAccesoModal = ({ open, onSuccess }) => {

    const [password, setPassword] = useState("");
    const [confirmarPassword, setConfirmarPassword] = useState("");

    const [mostrarPassword, setMostrarPassword] = useState(false);
    const [mostrarConfirmarPassword, setMostrarConfirmarPassword] =
        useState(false);

    const [isLoading, setIsLoading] = useState(false);


    // Requisitos de contraseña
    const tiene8Caracteres = password.length >= 8;
    const tieneMayuscula = /[A-Z]/.test(password);
    const tieneMinuscula = /[a-z]/.test(password);
    const tieneNumero = /[0-9]/.test(password);
    const tieneEspecial = /[^A-Za-z0-9]/.test(password);

    const passwordValida =
        tiene8Caracteres &&
        tieneMayuscula &&
        tieneMinuscula &&
        tieneNumero &&
        tieneEspecial;


    const cambiarPassword = async () => {

        // Validar contraseña
        if (!passwordValida) {
            return toast.error("La contraseña no cumple con los requisitos de seguridad.", toast_config);
        }

        // Validar confirmación
        if (!confirmarPassword) {
            return toast.error("Es necesario confirmar la contraseña.", toast_config);
        }

        if (password !== confirmarPassword) {
            return toast.error("Las contraseñas no coinciden.", toast_config);
        }


        try {

            setIsLoading(true);

            const token = sessionStorage.getItem("token");

            const decoded = jwtDecode(token);

            const jCambiarPass = {
                password,
                id_ahorrador: decoded.id_ahorrador
            }

            const { data } = await apiRc.put("/ahorrador/primer-acceso/cambiar-password", jCambiarPass, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/x-www-form-urlencoded',
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
            });

            sessionStorage.setItem( "token", data );

            toast.success("Contraseña actualizada correctamente.", toast_config);


            // Avisar a ClientesHome
            onSuccess(data);


        } catch (error) {

            console.error(error);

            let mensaje = "Ocurrió un error al cambiar la contraseña.";

            if (error.response?.data?.mensaje) {
                mensaje = error.response.data.mensaje;
            }

            toast.error(
                mensaje,
                toast_config
            );

        } finally {

            setIsLoading(false);

        }
    };


    const requisitoColor = (cumple) =>
        cumple
            ? "success.main"
            : "text.secondary";


    return (
        <Dialog
            open={open}
            fullWidth
            maxWidth="xs"

            // No permitir cerrar haciendo click afuera
            disableEscapeKeyDown

            slotProps={{
                backdrop: {
                    sx: {
                        backdropFilter: "blur(5px)",
                        backgroundColor:
                            "rgba(0,0,0,.65)",
                    },
                },
            }}

            PaperProps={{
                sx: {
                    borderRadius: 4,
                    p: 1,
                },
            }}
        >
            {isLoading && <LoadingSpinner />}
            {/* TÍTULO */}
            <DialogTitle
                sx={{
                    textAlign: "center",
                    fontWeight: 700,
                    color: "#517950",
                    pt: 3,
                }}
            >
                Primer acceso
            </DialogTitle>


            <DialogContent>

                <Typography
                    sx={{
                        textAlign: "center",
                        color: "text.secondary",
                        mb: 3,
                    }}
                >
                    Por seguridad, debes cambiar tu contraseña
                    antes de continuar.
                </Typography>


                {/* NUEVA CONTRASEÑA */}
                <TextField
                    fullWidth
                    label="Nueva contraseña"
                    type={
                        mostrarPassword
                            ? "text"
                            : "password"
                    }
                    value={password}
                    onChange={(e) =>
                        setPassword(e.target.value)
                    }
                    margin="normal"
                    autoComplete="new-password"

                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <LockOutlinedIcon />
                            </InputAdornment>
                        ),

                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    onClick={() =>
                                        setMostrarPassword(
                                            !mostrarPassword
                                        )
                                    }
                                    edge="end"
                                >
                                    {mostrarPassword ? (
                                        <VisibilityOffIcon />
                                    ) : (
                                        <VisibilityIcon />
                                    )}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />


                {/* REQUISITOS */}
                <Box
                    sx={{
                        mt: 2,
                        p: 2,
                        borderRadius: 2,
                        backgroundColor:
                            "rgba(81,121,80,.06)",
                    }}
                >

                    <Typography
                        variant="body2"
                        fontWeight={600}
                        mb={1}
                    >
                        La contraseña debe contener:
                    </Typography>


                    <Typography
                        variant="body2"
                        color={requisitoColor(
                            tiene8Caracteres
                        )}
                    >
                        {tiene8Caracteres ? "✓" : "•"}{" "}
                        Al menos 8 caracteres
                    </Typography>


                    <Typography
                        variant="body2"
                        color={requisitoColor(
                            tieneMayuscula
                        )}
                    >
                        {tieneMayuscula ? "✓" : "•"}{" "}
                        Una letra mayúscula
                    </Typography>


                    <Typography
                        variant="body2"
                        color={requisitoColor(
                            tieneMinuscula
                        )}
                    >
                        {tieneMinuscula ? "✓" : "•"}{" "}
                        Una letra minúscula
                    </Typography>


                    <Typography
                        variant="body2"
                        color={requisitoColor(
                            tieneNumero
                        )}
                    >
                        {tieneNumero ? "✓" : "•"}{" "}
                        Un número
                    </Typography>


                    <Typography
                        variant="body2"
                        color={requisitoColor(
                            tieneEspecial
                        )}
                    >
                        {tieneEspecial ? "✓" : "•"}{" "}
                        Un carácter especial
                    </Typography>

                </Box>


                {/* CONFIRMAR CONTRASEÑA */}
                <TextField
                    fullWidth
                    label="Confirmar contraseña"
                    type={
                        mostrarConfirmarPassword
                            ? "text"
                            : "password"
                    }
                    value={confirmarPassword}
                    onChange={(e) =>
                        setConfirmarPassword(
                            e.target.value
                        )
                    }
                    margin="normal"
                    autoComplete="new-password"

                    error={
                        confirmarPassword.length > 0 &&
                        password !== confirmarPassword
                    }

                    helperText={
                        confirmarPassword.length > 0 &&
                            password !== confirmarPassword
                            ? "Las contraseñas no coinciden"
                            : ""
                    }

                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <LockOutlinedIcon />
                            </InputAdornment>
                        ),

                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    onClick={() =>
                                        setMostrarConfirmarPassword(
                                            !mostrarConfirmarPassword
                                        )
                                    }
                                    edge="end"
                                >
                                    {mostrarConfirmarPassword ? (
                                        <VisibilityOffIcon />
                                    ) : (
                                        <VisibilityIcon />
                                    )}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />

            </DialogContent>


            {/* BOTÓN */}
            <DialogActions
                sx={{
                    px: 3,
                    pb: 3,
                }}
            >

                <Button
                    fullWidth
                    variant="contained"
                    onClick={cambiarPassword}
                    disabled={
                        isLoading ||
                        !passwordValida ||
                        password !== confirmarPassword
                    }
                    sx={{
                        py: 1.4,
                        borderRadius: 3,
                        background:
                            "linear-gradient(135deg,#2EB872,#1B7F52)",
                        fontWeight: 700,

                        "&:hover": {
                            background:
                                "linear-gradient(135deg,#36C97D,#208E5B)",
                        },

                        "&.Mui-disabled": {
                            background:
                                "rgba(0,0,0,.12)",
                        },
                    }}
                >
                    {isLoading
                        ? "Actualizando..."
                        : "Cambiar contraseña"}
                </Button>

            </DialogActions>
            <ToastContainer
                transition={Flip}
                position="bottom-center"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="colored"
            />
        </Dialog>
    );
};


export default PrimerAccesoModal;

